import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { logoutRequest } from '../redux/ducks/UserDucks';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import { getRestaurants } from '../services/RestaurantServices';
import storage from '../utils/storage';
import { setBugsnagRestaurant } from '../utils/bugsnag/userContext';

export const MenuContext = createContext({});

export const useMenuContext = () => useContext(MenuContext);

export function MenuContextProvider({ children }) {
  const dispatch = useDispatch();
  const expireSession = useCallback(() => dispatch(logoutRequest()), [dispatch]);
  const identity = useSelector((state) => state.user.user);
  const sessionGeneration = useSelector((state) => state.user.sessionGeneration);
  const sessionPending = useSelector((state) => state.user.sessionPending);
  const sessionKey = identity && !sessionPending ? JSON.stringify([identity.id, sessionGeneration || 0]) : null;
  const sessionRef = useRef(sessionKey);
  sessionRef.current = sessionKey;
  const [discoveryScope, setDiscoveryScope] = useState(null);
  const [discoveryError, setDiscoveryError] = useState(null);
  const controllerRef = useRef(null);
  const location = useLocation();
  const [pages, setPages] = useState([]);
  const [selectedPageByPath, setSelectedPageByPath] = useState({});
  const [selectedRestaurant, setSelectedRestaurant] = useState();
  const [restaurantsList, setRestaurantsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const restaurantRequest = useRef(0);

  const selectedPageKey = selectedPageByPath[location.pathname];
  const selectedPage = selectedPageKey === undefined ? 0 :
    pages.findIndex((page) => (page.id || page.label) === selectedPageKey);

  const setPage = useCallback(
    (idx) => {
      const key = pages[idx]?.id || pages[idx]?.label;
      if (!key) return;
      setSelectedPageByPath((prev) => ({
        ...prev,
        [location.pathname]: key,
      }));
    },
    [location.pathname, pages]
  );

  const reset = useCallback(() => {
    setPages([]);
  }, [setPages]);

  const handleSetSelectedRestaurant = useCallback(async ({ id, refresh = false }) => {
    const request = ++restaurantRequest.current;
    const scope = sessionKey;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const current = () => request === restaurantRequest.current && scope === sessionRef.current;
    if (!scope) return;
    try {
      setIsLoading(true);
      setDiscoveryError(null);

      const response = await getRestaurants({ signal: controller.signal });
      if (!current()) return;
      setDiscoveryScope(scope);
      setRestaurantsList(response);

      let restaurant = response.find((rest) => String(rest.id) === String(id));
      if (_.isNil(restaurant) && !refresh) {
        restaurant = response[0];
      }
      if (!restaurant && refresh) restaurant = { id };

      if (_.isNil(restaurant)) {
        setError(true);
        setBugsnagRestaurant(null);
      } else {
        setSelectedRestaurant(restaurant);
        setBugsnagRestaurant(restaurant);

        window.businessTimezone = restaurant.timezone;

        setError(false);
      }
    } catch (err) {
      if (!current()) return;
      setDiscoveryScope(scope);
      setDiscoveryError(err);
      if (Number(err?.status) === 401) expireSession();
      if (!refresh) setError(true);
      setBugsnagRestaurant(null);
    } finally {
      if (current()) setIsLoading(false);
    }
    // dispatch is stable in the Redux provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  useEffect(() => {
    let active = true;
    (async () => {
      const id = await storage.get('selectedRestaurantId');
      if (active) await handleSetSelectedRestaurant({ id });
    })();
    return () => {
      active = false;
      restaurantRequest.current += 1;
      controllerRef.current?.abort();
    };
  }, [handleSetSelectedRestaurant]);

  const refreshRestaurants = useCallback(() => handleSetSelectedRestaurant({
    id: selectedRestaurant?.id, refresh: selectedRestaurant?.id != null,
  }), [handleSetSelectedRestaurant, selectedRestaurant?.id]);
  const scopeCurrent = sessionKey && discoveryScope === sessionKey;

  return (
    <MenuContext.Provider
      value={{
        setPages,
        pages,
        selectedPage,
        setPage,
        selectedPageKey,
        reset,
        selectedRestaurant: scopeCurrent ? selectedRestaurant : undefined,
        restaurantsList: scopeCurrent ? restaurantsList : [],
        discoveryError: scopeCurrent ? discoveryError : null,
        sessionKey,
        expireSession,
        refreshRestaurants,
        setSelectedRestaurant: handleSetSelectedRestaurant,
        isLoading: !!sessionKey && (!scopeCurrent || isLoading),
        error: scopeCurrent ? error : false,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
}

MenuContextProvider.propTypes = {
  children: PropTypes.node,
};

export function Component({ components }) {
  const { selectedPageKey, setPages, reset } = useMenuContext();
  // Register navigation metadata only, not React elements. Inline element arrays
  // are common callers; comparing metadata avoids a context update/render loop.
  useLayoutEffect(() => {
    setPages((previous) => {
      const next = components.map(({ id, label }) => ({ id, label }));
      return JSON.stringify(previous) === JSON.stringify(next) ? previous : next;
    });
  }, [components, setPages]);

  useLayoutEffect(() => {
    return () => reset();
  }, [reset]);

  const selected = selectedPageKey === undefined ? components[0] :
    components.find((page) => (page.id || page.label) === selectedPageKey);
  return <>{selected?.component || <p role="status">This page is not available for the selected restaurant. Choose a page from the menu.</p>}</>;
}

Component.propTypes = {
  components: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      component: PropTypes.node,
    })
  ),
};
